import React from "react"
import { Box, Typography } from "@mui/material"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { formatLatLngDM } from "@/utils/geoFormat"
import { CAUTION, CAUTION_TINT, DANGER, DANGER_TINT, KPI_TILE_BG, PANEL_DIVIDER, PANEL_TEXT_MUTED, WW_BLUE } from "@/theme/tokens"
import { FIELD_LABEL_SX } from "@/theme/panelStyles"
dayjs.extend(utc)

export const RED = { color: DANGER, tint: DANGER_TINT }
export const ORANGE = { color: CAUTION, tint: CAUTION_TINT }
export const INK = WW_BLUE
export const MUTED = PANEL_TEXT_MUTED
export const DIVIDER = PANEL_DIVIDER
export const TRACK = "#edf1f4"
export const IN_LIMIT = "#b9c6d1"

export const SECTION_LABEL_SX = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }
export const TILE_BG = KPI_TILE_BG
export const SMALL_LABEL_SX = { fontSize: FIELD_LABEL_SX.fontSize, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: MUTED }

export const fmt = (value, unit) =>
  value == null ? "–" : unit === "kn" ? Math.round(value).toFixed(0) : value.toFixed(1)

const clock = (iso, approx = true) =>
  iso ? `${approx ? "~" : ""}${dayjs.utc(iso).format("HH:mm")}` : "–"

export function IconCircle({ Icon, palette, size }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: "50%",
        background: palette.tint,
        color: palette.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
    </Box>
  )
}

export function LimitBar({ max, limit, unit, color, compact }) {
  if (limit == null) return null
  const scale = Math.max(max ?? limit, limit) * 1.2 || 1
  const pct = (v) => `${(v / scale) * 100}%`
  return (
    <Box sx={{ position: "relative", mt: compact ? 1.75 : 2, height: 8 }}>
      <Box sx={{ position: "absolute", inset: 0, borderRadius: 4, background: TRACK, overflow: "hidden" }}>
        <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: pct(Math.min(max ?? limit, limit)), background: IN_LIMIT }} />
        {max != null && max > limit && (
          <Box sx={{ position: "absolute", left: pct(limit), top: 0, bottom: 0, width: pct(max - limit), background: color }} />
        )}
      </Box>
      <Box sx={{ position: "absolute", left: pct(limit), top: -2, bottom: -2, width: 2, ml: "-1px", background: INK }} />
      <Typography
        sx={{ position: "absolute", left: pct(limit), bottom: 12, transform: "translateX(-50%)", fontSize: 10, color: MUTED, whiteSpace: "nowrap" }}
      >
        limit {fmt(limit, unit)}
      </Typography>
    </Box>
  )
}

function TimelineBar({ color, durationH, compact }) {
  const dot = (filled) => ({
    width: 12,
    height: 12,
    borderRadius: "50%",
    boxSizing: "border-box",
    border: `3px solid ${color}`,
    background: filled ? color : "white",
    flex: "none",
  })
  return (
    <Box sx={{ position: "relative", display: "flex", alignItems: "center", height: compact ? 18 : 22 }}>
      <Box sx={dot(false)} />
      <Box sx={{ flex: 1, height: 3, background: color }} />
      <Box sx={dot(true)} />
      {durationH != null && (
        <Typography
          component="span"
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            px: 0.75,
            height: compact ? 18 : 20,
            display: "flex",
            alignItems: "center",
            borderRadius: 10,
            background: "white",
            border: `1.5px solid ${color}`,
            fontSize: 12,
            fontWeight: 700,
            color: INK,
          }}
        >
          {durationH.toFixed(1)} h
        </Typography>
      )}
    </Box>
  )
}

function EndpointColumn({ label, end, routeEnd, align, compact, approx }) {
  const sx = { textAlign: align }
  const atEnd = end?.[routeEnd.flag]
  if (compact) {
    return (
      <Box sx={sx}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase" }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK }}>
          {atEnd ? routeEnd.text : `${clock(end?.time, approx)} ${end?.time ? dayjs.utc(end.time).format("D MMM") : ""}`}
        </Typography>
      </Box>
    )
  }
  return (
    <Box sx={sx}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase" }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: INK, lineHeight: 1.3 }}>
        {atEnd ? routeEnd.text : clock(end?.time, approx)}
      </Typography>
      {end?.time && (
        <Typography sx={{ fontSize: 11, color: MUTED }}>
          {atEnd ? `${clock(end.time, approx)} ` : ""}
          {dayjs.utc(end.time).format("ddd D MMM")}
        </Typography>
      )}
      {end?.leg && <Typography sx={{ fontSize: 11, fontWeight: 600, color: INK }}>{end.leg}</Typography>}
      {end?.position && (
        <Typography sx={{ fontSize: 10, color: MUTED, fontVariantNumeric: "tabular-nums" }}>
          {formatLatLngDM(end.position[0], end.position[1])}
        </Typography>
      )}
    </Box>
  )
}

export function Timeline({ entry, exit, durationH, color, compact = false, labels = ["Entry", "Exit"], approx = true }) {
  if (!entry && !exit) return null
  return (
    <Box sx={{ mt: compact ? 0.75 : 1.5 }}>
      <TimelineBar color={color} durationH={durationH} compact={compact} />
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: compact ? 0.25 : 0.5 }}>
        <EndpointColumn label={labels[0]} end={entry} routeEnd={{ flag: "atDeparture", text: "At departure" }} align="left" compact={compact} approx={approx} />
        <EndpointColumn label={labels[1]} end={exit} routeEnd={{ flag: "atArrival", text: "At arrival" }} align="right" compact={compact} approx={approx} />
      </Box>
    </Box>
  )
}
